import { useEffect, useState } from 'react';
import {
  createWorkspaceUser,
  deleteWorkspaceUser,
  listWorkspaceUsers,
  resendWorkspaceUserInvite,
  resetWorkspaceUserPassword,
  transferWorkspaceOwnership,
  updateWorkspaceUserIdentity,
  updateWorkspaceUserRole,
  updateWorkspaceUserStatus,
} from '../services/workspace-users.service';
import { archiveStaffByWorkspaceUser, listStaffResult } from '@/modules/staff/services/staff.service';

const INITIAL_CREATE_FORM = {
  fullName: '',
  email: '',
  permissionRole: 'STAFF',
  teamType: 'INTERNAL',
  companyRole: '',
  password: '',
  confirmPassword: '',
};

const CREATEABLE_ROLE_OPTIONS = ['MANAGER', 'STAFF'];
const TEAM_TYPE_OPTIONS = ['INTERNAL', 'EXTERNAL'];

const INITIAL_PASSWORD_RESET_FORM = {
  userId: '',
  password: '',
  confirmPassword: '',
};

const INITIAL_EDIT_FORM = {
  userId: '',
  fullName: '',
  email: '',
  teamType: 'INTERNAL',
  companyRole: '',
};

const INITIAL_FILTERS = {
  query: '',
  teamType: [],
  permissionRole: [],
  status: [],
  inviteStatus: [],
};
const WORKSPACE_USERS_AUTH_ERRORS = new Set([
  'WORKSPACE_USERS_AUTH_REQUIRED',
  'WORKSPACE_USERS_SESSION_EXPIRED',
  'WORKSPACE_USERS_SESSION_REFRESH_REQUIRED',
]);

export function useWorkspaceUsersData() {
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [usersUnavailable, setUsersUnavailable] = useState(false);
  const [usersAuthRequired, setUsersAuthRequired] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [pendingUserId, setPendingUserId] = useState('');
  const [pendingRoleUserId, setPendingRoleUserId] = useState('');
  const [createForm, setCreateForm] = useState(INITIAL_CREATE_FORM);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [createdInviteLink, setCreatedInviteLink] = useState('');
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [passwordResetForm, setPasswordResetForm] = useState(INITIAL_PASSWORD_RESET_FORM);
  const [passwordResetError, setPasswordResetError] = useState('');
  const [passwordResetSuccess, setPasswordResetSuccess] = useState('');
  const [pendingPasswordUserId, setPendingPasswordUserId] = useState('');
  const [ownershipCandidate, setOwnershipCandidate] = useState(null);
  const [ownershipError, setOwnershipError] = useState('');
  const [ownershipSuccess, setOwnershipSuccess] = useState('');
  const [pendingOwnershipUserId, setPendingOwnershipUserId] = useState('');
  const [editForm, setEditForm] = useState(INITIAL_EDIT_FORM);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [pendingEditUserId, setPendingEditUserId] = useState('');
  const [pendingDeleteUserId, setPendingDeleteUserId] = useState('');
  const [pendingInviteUserId, setPendingInviteUserId] = useState('');
  const [linkedStaffByUserId, setLinkedStaffByUserId] = useState({});
  const [linkedStaffError, setLinkedStaffError] = useState('');
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  function applyLinkedStaffResult(result) {
    if (!result.ok) {
      setLinkedStaffByUserId({});
      setLinkedStaffError(result.message || result.error || 'Collegamenti staff non disponibili.');
      return;
    }

    const nextLinkedStaff = {};
    result.data.forEach((staffMember) => {
      if (staffMember.linkedWorkspaceUserId) {
        nextLinkedStaff[String(staffMember.linkedWorkspaceUserId)] = {
          id: staffMember.id,
          name: staffMember.name,
          email: staffMember.email,
        };
      }
    });
    setLinkedStaffByUserId(nextLinkedStaff);
    setLinkedStaffError('');
  }

  async function refreshLinkedStaff() {
    applyLinkedStaffResult(await listStaffResult());
  }

  async function refreshUsers() {
    const result = await listWorkspaceUsers();

    if (!result.ok) {
      setUsers([]);
      setUsersUnavailable(result.error === 'WORKSPACE_USERS_UNAVAILABLE');
      setUsersAuthRequired(WORKSPACE_USERS_AUTH_ERRORS.has(result.error));
      setUsersError(result.message || result.error || '');
      return false;
    }

    setUsers(result.data.users);
    setUsersUnavailable(false);
    setUsersAuthRequired(false);
    setUsersError('');
    await refreshLinkedStaff();
    return true;
  }

  useEffect(() => {
    let active = true;

    async function loadUsers() {
      if (!active) {
        return;
      }

      setIsLoadingUsers(true);
      const [result, staffResult] = await Promise.all([
        listWorkspaceUsers(),
        listStaffResult(),
      ]);

      if (!active) {
        return;
      }

      if (!result.ok) {
        setUsers([]);
        setUsersUnavailable(result.error === 'WORKSPACE_USERS_UNAVAILABLE');
        setUsersAuthRequired(WORKSPACE_USERS_AUTH_ERRORS.has(result.error));
        setUsersError(result.message || result.error || '');
        setIsLoadingUsers(false);
        return;
      }

      setUsers(result.data.users);
      applyLinkedStaffResult(staffResult);
      setUsersUnavailable(false);
      setUsersAuthRequired(false);
      setUsersError('');
      setIsLoadingUsers(false);
    }

    loadUsers();

    return () => {
      active = false;
    };
  }, []);

  function updateCreateFormField(field, value) {
    setCreateForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  }

  function validateCreateForm() {
    if (!createForm.fullName.trim()) {
      return 'Inserisci nome e cognome.';
    }

    if (!createForm.email.trim() || !createForm.email.includes('@')) {
      return 'Inserisci una email valida.';
    }

    if (!CREATEABLE_ROLE_OPTIONS.includes(createForm.permissionRole)) {
      return 'Permesso applicativo non valido.';
    }

    if (!TEAM_TYPE_OPTIONS.includes(createForm.teamType)) {
      return 'Tipo team non valido.';
    }

    if (!createForm.companyRole.trim()) {
      return 'Inserisci il ruolo aziendale.';
    }

    if (!createForm.password || createForm.password.length < 8) {
      return 'Inserisci una password iniziale di almeno 8 caratteri.';
    }

    if (createForm.password !== createForm.confirmPassword) {
      return 'Le password iniziali non coincidono.';
    }

    return '';
  }

  async function createUser() {
    setCreateError('');
    setCreateSuccess('');

    const validationError = validateCreateForm();
    if (validationError) {
      setCreateError(validationError);
      return false;
    }

    setIsCreatingUser(true);
    const result = await createWorkspaceUser({
      fullName: createForm.fullName.trim(),
      email: createForm.email.trim(),
      role: createForm.permissionRole,
      teamType: createForm.teamType,
      companyRole: createForm.companyRole.trim(),
      password: createForm.password,
    });
    setIsCreatingUser(false);

    if (!result.ok) {
      setCreateError(result.message || result.error || '');
      return false;
    }

    await refreshUsers();
    setCreateForm(INITIAL_CREATE_FORM);
    const inviteToken = result.data?.inviteToken || result.data?.user?.inviteToken || '';
    const invitePath = inviteToken ? `/accept-invite/${inviteToken}` : '';
    const inviteLink = invitePath && typeof window !== 'undefined'
      ? `${window.location.origin}${invitePath}`
      : invitePath;
    setCreatedInviteLink(inviteLink);
    setCreateSuccess(inviteLink ? 'Utente creato. Copia il link invito e consegnalo insieme alla password iniziale.' : 'Utente workspace creato correttamente.');
    return true;
  }

  async function removeUser(user) {
    setUsersError('');
    setPendingDeleteUserId(String(user.id));

    const archiveResult = await archiveStaffByWorkspaceUser(user.id);
    if (!archiveResult.ok && archiveResult.error !== 'STAFF_API_UNAVAILABLE') {
      setPendingDeleteUserId('');
      setUsersError(archiveResult.message || archiveResult.error || 'Archiviazione staff non completata.');
      return false;
    }

    const result = await deleteWorkspaceUser(user.id);
    setPendingDeleteUserId('');

    if (!result.ok) {
      setUsersUnavailable(result.error === 'WORKSPACE_USER_DELETE_UNAVAILABLE');
      setUsersAuthRequired(WORKSPACE_USERS_AUTH_ERRORS.has(result.error));
      setUsersError(result.message || result.error || '');
      return false;
    }

    await refreshUsers();
    return true;
  }

  function openPasswordReset(user) {
    setPasswordResetForm({
      userId: String(user.id),
      password: '',
      confirmPassword: '',
    });
    setPasswordResetError('');
    setPasswordResetSuccess('');
  }

  function closePasswordReset() {
    setPasswordResetForm(INITIAL_PASSWORD_RESET_FORM);
    setPasswordResetError('');
  }

  function updatePasswordResetField(field, value) {
    setPasswordResetForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  }

  function validatePasswordResetForm() {
    if (!passwordResetForm.userId) {
      return 'Seleziona un utente.';
    }

    if (!passwordResetForm.password.trim()) {
      return 'Inserisci la nuova password.';
    }

    if (passwordResetForm.password !== passwordResetForm.confirmPassword) {
      return 'Le password non coincidono.';
    }

    return '';
  }

  async function resetUserPassword() {
    setPasswordResetError('');
    setPasswordResetSuccess('');

    const validationError = validatePasswordResetForm();
    if (validationError) {
      setPasswordResetError(validationError);
      return false;
    }

    setPendingPasswordUserId(passwordResetForm.userId);
    const result = await resetWorkspaceUserPassword(passwordResetForm.userId, passwordResetForm.password);
    setPendingPasswordUserId('');

    if (!result.ok) {
      setPasswordResetError(result.message || result.error || '');
      return false;
    }

    setPasswordResetForm(INITIAL_PASSWORD_RESET_FORM);
    setPasswordResetSuccess('Password utente reimpostata correttamente.');
    return true;
  }

  async function resendUserInvite(user) {
    setUsersError('');
    setPendingInviteUserId(String(user.id));
    const result = await resendWorkspaceUserInvite(user.id);
    setPendingInviteUserId('');

    if (!result.ok) {
      setUsersUnavailable(result.error === 'WORKSPACE_USER_INVITE_RESEND_UNAVAILABLE');
      setUsersAuthRequired(WORKSPACE_USERS_AUTH_ERRORS.has(result.error));
      setUsersError(result.message || result.error || '');
      return false;
    }

    await refreshUsers();
    const invitePath = result.data?.inviteToken ? `/accept-invite/${result.data.inviteToken}` : '';
    const inviteLink = invitePath && typeof window !== 'undefined'
      ? `${window.location.origin}${invitePath}`
      : invitePath;
    setCreatedInviteLink(inviteLink);
    setCreateSuccess(inviteLink ? "Invito reinviato. Copia il nuovo link e consegnalo all'utente." : 'Invito reinviato correttamente.');
    return true;
  }

  async function toggleUserStatus(user) {
    setPendingUserId(String(user.id));
    setUsersError('');

    const result = await updateWorkspaceUserStatus(user.id, !user.active);

    setPendingUserId('');

    if (!result.ok) {
      setUsersUnavailable(result.error === 'WORKSPACE_USER_STATUS_UNAVAILABLE');
      setUsersAuthRequired(WORKSPACE_USERS_AUTH_ERRORS.has(result.error));
      setUsersError(result.message || result.error || '');
      return false;
    }

    setUsers((currentUsers) =>
      currentUsers.map((currentUser) =>
        currentUser.id === user.id ? result.data.user : currentUser
      )
    );
    setUsersUnavailable(false);
    setUsersAuthRequired(false);
    setUsersError('');
    return true;
  }

  async function changeUserRole(user, role) {
    setPendingRoleUserId(String(user.id));
    setUsersError('');

    const result = await updateWorkspaceUserRole(user.id, role);

    setPendingRoleUserId('');

    if (!result.ok) {
      setUsersUnavailable(result.error === 'WORKSPACE_USER_ROLE_UNAVAILABLE');
      setUsersAuthRequired(WORKSPACE_USERS_AUTH_ERRORS.has(result.error));
      setUsersError(result.message || result.error || '');
      return false;
    }

    setUsers((currentUsers) =>
      currentUsers.map((currentUser) =>
        currentUser.id === user.id ? result.data.user : currentUser
      )
    );
    setUsersUnavailable(false);
    setUsersAuthRequired(false);
    setUsersError('');
    return true;
  }

  function openUserEdit(user) {
    setEditForm({
      userId: String(user.id),
      fullName: user.fullName || '',
      email: user.email || '',
      teamType: user.teamType || 'INTERNAL',
      companyRole: user.companyRole || '',
    });
    setEditError('');
    setEditSuccess('');
  }

  function closeUserEdit() {
    setEditForm(INITIAL_EDIT_FORM);
    setEditError('');
  }

  function updateEditFormField(field, value) {
    setEditForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  }

  function validateEditForm() {
    if (!editForm.userId) {
      return 'Seleziona un utente.';
    }

    if (!editForm.fullName.trim()) {
      return 'Inserisci nome e cognome.';
    }

    if (!editForm.email.trim() || !editForm.email.includes('@')) {
      return 'Inserisci una email valida.';
    }

    if (!TEAM_TYPE_OPTIONS.includes(editForm.teamType)) {
      return 'Tipo team non valido.';
    }

    return '';
  }

  async function updateUserIdentity() {
    setEditError('');
    setEditSuccess('');

    const validationError = validateEditForm();
    if (validationError) {
      setEditError(validationError);
      return false;
    }

    setPendingEditUserId(editForm.userId);
    const result = await updateWorkspaceUserIdentity(editForm.userId, {
      fullName: editForm.fullName.trim(),
      email: editForm.email.trim(),
      teamType: editForm.teamType,
      companyRole: editForm.companyRole.trim(),
    });
    setPendingEditUserId('');

    if (!result.ok) {
      setUsersUnavailable(result.error === 'WORKSPACE_USER_IDENTITY_UNAVAILABLE');
      setUsersAuthRequired(WORKSPACE_USERS_AUTH_ERRORS.has(result.error));
      setEditError(result.message || result.error || '');
      return false;
    }

    setUsers((currentUsers) =>
      currentUsers.map((currentUser) =>
        currentUser.id === result.data.user.id ? result.data.user : currentUser
      )
    );
    setUsersUnavailable(false);
    setUsersAuthRequired(false);
    setUsersError('');
    setEditForm(INITIAL_EDIT_FORM);
    setEditSuccess('Utente workspace aggiornato correttamente.');
    return true;
  }

  function openOwnershipTransfer(user) {
    setOwnershipCandidate(user);
    setOwnershipError('');
    setOwnershipSuccess('');
  }

  function closeOwnershipTransfer() {
    setOwnershipCandidate(null);
    setOwnershipError('');
  }

  async function transferOwnership() {
    if (!ownershipCandidate?.id) {
      setOwnershipError('Seleziona un utente idoneo.');
      return false;
    }

    setPendingOwnershipUserId(String(ownershipCandidate.id));
    setOwnershipError('');
    setOwnershipSuccess('');

    const result = await transferWorkspaceOwnership(ownershipCandidate.id);

    setPendingOwnershipUserId('');

    if (!result.ok) {
      setUsersUnavailable(result.error === 'WORKSPACE_OWNERSHIP_UNAVAILABLE');
      setUsersAuthRequired(WORKSPACE_USERS_AUTH_ERRORS.has(result.error));
      setOwnershipError(result.message || result.error || '');
      return false;
    }

    setUsers(result.data.users);
    setUsersUnavailable(false);
    setUsersAuthRequired(false);
    setUsersError('');
    setOwnershipCandidate(null);
    setOwnershipSuccess('Ownership trasferita correttamente.');
    return true;
  }

  function updateFilters(nextFilters) {
    setFilters((currentFilters) => ({
      ...currentFilters,
      ...nextFilters,
    }));
  }

  function resetFilters() {
    setFilters(INITIAL_FILTERS);
  }

  function matchesMultiFilter(value, selectedValues) {
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
      return true;
    }
    return selectedValues.includes(value);
  }

  function getFilteredUsers() {
    const query = filters.query.trim().toLowerCase();
    return users.filter((user) => {
      const inviteStatus = normalizeInviteStatus(user);
      const status = user.active ? 'ACTIVE' : 'INACTIVE';
      const searchable = [
        user.fullName,
        user.email,
        user.companyRole,
        user.permissionRole || user.role,
      ].join(' ').toLowerCase();

      return (!query || searchable.includes(query))
        && matchesMultiFilter(user.teamType || 'INTERNAL', filters.teamType)
        && matchesMultiFilter(user.permissionRole || user.role, filters.permissionRole)
        && matchesMultiFilter(status, filters.status)
        && matchesMultiFilter(inviteStatus, filters.inviteStatus);
    });
  }

  function normalizeInviteStatus(user) {
    if (user.inviteStatus) {
      return user.inviteStatus === 'INVITED' ? 'PENDING' : user.inviteStatus;
    }
    return user.active ? 'ACCEPTED' : 'PENDING';
  }

  return {
    users,
    filteredUsers: getFilteredUsers(),
    filters,
    isLoadingUsers,
    usersUnavailable,
    usersAuthRequired,
    usersError,
    usersCatalogStatus: usersAuthRequired
      ? 'auth_required'
      : usersUnavailable
        ? 'unavailable'
        : users.length > 0
        ? 'available'
        : 'empty',
    pendingUserId,
    pendingRoleUserId,
    pendingPasswordUserId,
    pendingOwnershipUserId,
    pendingEditUserId,
    pendingDeleteUserId,
    pendingInviteUserId,
    createForm,
    createError,
    createSuccess,
    createdInviteLink,
    isCreatingUser,
    passwordResetForm,
    passwordResetError,
    passwordResetSuccess,
    ownershipCandidate,
    ownershipError,
    ownershipSuccess,
    editForm,
    editError,
    editSuccess,
    linkedStaffByUserId,
    linkedStaffError,
    updateCreateFormField,
    createUser,
    removeUser,
    openPasswordReset,
    closePasswordReset,
    updatePasswordResetField,
    resetUserPassword,
    resendUserInvite,
    toggleUserStatus,
    changeUserRole,
    openUserEdit,
    closeUserEdit,
    updateEditFormField,
    updateUserIdentity,
    openOwnershipTransfer,
    closeOwnershipTransfer,
    transferOwnership,
    updateFilters,
    resetFilters,
  };
}
